import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../config/api';
import {
  loadFlowAlertSettings,
  saveFlowAlertSettings,
} from '../utils/flowAlertSettingsStore';

interface WatchlistContextType {
  watchlist: string[];
  aiAnalysisEnabled: boolean;
  analysisLanguage: 'en' | 'ar';
  addToWatchlist: (symbol: string) => void;
  removeFromWatchlist: (symbol: string) => void;
  setWatchlist: (symbols: string[]) => void;
  toggleAiAnalysis: () => void;
  setAiAnalysisEnabled: (enabled: boolean) => void;
  setAnalysisLanguage: (lang: 'en' | 'ar') => void;
  saveWatchlist: () => Promise<void>;
  loadWatchlist: () => Promise<void>;
  isLoading: boolean;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within WatchlistProvider');
  }
  return context;
};

interface WatchlistProviderProps {
  children: React.ReactNode;
}

export const WatchlistProvider: React.FC<WatchlistProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [watchlist, setWatchlistState] = useState<string[]>([]);
  // Quiet by default: no automatic AI watchlist-analysis popups until the user
  // turns analysis on in the watchlist manager.
  const [aiAnalysisEnabled, setAiAnalysisEnabledState] = useState<boolean>(false);
  const [analysisLanguage, setAnalysisLanguageState] = useState<'en' | 'ar'>('en');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load watchlist on mount
  useEffect(() => {
    if (token) {
      loadWatchlist();
    }
  }, [token]);

  // Keep alert profile watchlist symbols in sync with user watchlist.
  useEffect(() => {
    try {
      const cfg = loadFlowAlertSettings();
      const normalized = (watchlist || [])
        .map((s) => String(s || '').trim().toUpperCase())
        .filter(Boolean);
      const prev = (cfg.watchlist_symbols || [])
        .map((s) => String(s || '').trim().toUpperCase())
        .filter(Boolean);
      const same =
        normalized.length === prev.length &&
        normalized.every((s, i) => s === prev[i]);
      if (!same) {
        saveFlowAlertSettings({
          ...cfg,
          watchlist_symbols: normalized,
        });
      }
    } catch {
      /* ignore */
    }
  }, [watchlist]);

  const loadWatchlist = useCallback(async () => {
    if (!token) return;

    const timeoutMs = 15000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      setIsLoading(true);
      const response = await fetch(getApiUrl('/api/ai/watchlist/load'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (response.status === 429) {
        console.warn('Watchlist load rate limited');
        return;
      }

      if (response.ok) {
        const text = await response.text();
        let data: { success?: boolean; watchlist?: string[]; ai_analysis_enabled?: boolean; analysis_language?: string } = {};
        try {
          data = text && text.trim() ? JSON.parse(text) : {};
        } catch {
          data = {};
        }
        if (data.success) {
          const list = Array.isArray(data.watchlist) ? data.watchlist.filter((x): x is string => typeof x === 'string') : [];
          setWatchlistState(list);
          // Opt-in: only on when the server explicitly stored it as enabled.
          setAiAnalysisEnabledState(data.ai_analysis_enabled === true);
          setAnalysisLanguageState(data.analysis_language === 'ar' ? 'ar' : 'en');
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if ((error as Error)?.name !== 'AbortError') {
        console.warn('Watchlist load failed (timeout or network); using empty list.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const saveWatchlist = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl('/api/ai/watchlist/save'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          watchlist,
          ai_analysis_enabled: aiAnalysisEnabled,
          analysis_language: analysisLanguage
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.success;
      }
    } catch (error) {
      console.error('Error saving watchlist:', error);
      return false;
    }
  }, [token, watchlist, aiAnalysisEnabled, analysisLanguage]);

  const addToWatchlist = useCallback((symbol: string) => {
    const upperSymbol = symbol.toUpperCase();
    setWatchlistState(prev => {
      if (!prev.includes(upperSymbol)) {
        const newList = [...prev, upperSymbol];
        saveWatchlist(); // Auto-save
        return newList;
      }
      return prev;
    });
  }, [saveWatchlist]);

  const removeFromWatchlist = useCallback((symbol: string) => {
    const upperSymbol = symbol.toUpperCase();
    setWatchlistState(prev => {
      const newList = prev.filter(s => s !== upperSymbol);
      saveWatchlist(); // Auto-save
      return newList;
    });
  }, [saveWatchlist]);

  const setWatchlist = useCallback((symbols: string[]) => {
    setWatchlistState(symbols.map(s => s.toUpperCase()));
    saveWatchlist(); // Auto-save
  }, [saveWatchlist]);

  const toggleAiAnalysis = useCallback(() => {
    setAiAnalysisEnabledState(prev => {
      const newValue = !prev;
      saveWatchlist(); // Auto-save
      return newValue;
    });
  }, [saveWatchlist]);

  const setAiAnalysisEnabled = useCallback((enabled: boolean) => {
    setAiAnalysisEnabledState(enabled);
    saveWatchlist(); // Auto-save
  }, [saveWatchlist]);

  const setAnalysisLanguage = useCallback((lang: 'en' | 'ar') => {
    setAnalysisLanguageState(lang);
    saveWatchlist(); // Auto-save
  }, [saveWatchlist]);

  return (
    <WatchlistContext.Provider
      value={{
        watchlist,
        aiAnalysisEnabled,
        analysisLanguage,
        addToWatchlist,
        removeFromWatchlist,
        setWatchlist,
        toggleAiAnalysis,
        setAiAnalysisEnabled,
        setAnalysisLanguage,
        saveWatchlist,
        loadWatchlist,
        isLoading
      }}
    >
      {children}
    </WatchlistContext.Provider>
  );
};
